import { FC, useEffect } from 'react';

import { borderRadius, Flexbox, Text } from '@morressier/ts';

import styled from 'styled-components';
import useSwr, { mutate } from 'swr';

import { standaloneFetcher, VIDEO_API_URL } from 'api/livestream-rtmp';
import HeartLoadingSpinner from 'components/HeartLoadingSpinner';
import { PusherHandler } from 'store/modules/pusher';
import { logEvent } from 'utils/eventTracking';
import {
  RTMP_LIVE_STREAM_STATUS_ACTIVE,
  RTMP_LIVE_STREAM_STATUS_DISCONNECTED,
  RTMP_LIVE_STREAM_STATUS_IDLE,
  RTMP_LIVE_STREAM_STATUS_DISABLED,
} from 'utils/pusher';

import { CountDown } from '../Countdown';
import { useSessionTime } from '../hooks';
import { VideoPlayerRTMP } from '../NewVideoPlayer';

const VideoWrap = styled.div`
  ${borderRadius('small')};
  background: rgb(31, 34, 44);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 5rem;
`;

const Rtmp: FC<{
  session: MorressierSession;
  userId: string;
}> = props => {
  const { session, userId } = props;
  const { isPreSession } = useSessionTime({
    startDate: session.start_date,
    endDate: session.end_date,
    options: { runPastDates: true },
  });

  const rtmpStatusActive = RTMP_LIVE_STREAM_STATUS_ACTIVE(session._id);
  const rtmpStatusDisabled = RTMP_LIVE_STREAM_STATUS_DISABLED(session._id);

  const {
    data: rtmpStream,
    error: rtmpStreamError,
    mutate: boundMutate,
  } = useSwr<RtmpStream, Error>(`${VIDEO_API_URL}/${session._id}`, standaloneFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const handleEnded = e => {
    if (e.type === 'ended') {
      if (rtmpStream) {
        boundMutate({ ...rtmpStream, mux_stream_status: 'idle' }, false);
      }
    }
  };

  if (rtmpStreamError) {
    return (
      <VideoWrap>
        <Flexbox justifyContent="center" alignItems="center">
          <Text fontWeight="semiBold" as="span" color="white">
            {rtmpStreamError.message}
          </Text>
        </Flexbox>
      </VideoWrap>
    );
  }

  if (!rtmpStream)
    return (
      <VideoWrap>
        <Flexbox justifyContent="center" alignItems="center">
          <HeartLoadingSpinner />
        </Flexbox>
      </VideoWrap>
    );
  const renderStream = () => {
    switch (rtmpStream.mux_stream_status) {
      case 'idle': {
        if (rtmpStream.stream_started_at) {
          return (
            <VideoWrap>
              <Flexbox justifyContent="center" alignItems="center">
                <Text size="h4_new" fontWeight="bold" as="span" color="white">
                  Thank you for watching
                </Text>
              </Flexbox>
            </VideoWrap>
          );
        }
        if (isPreSession) {
          return null;
        }
        return (
          <VideoWrap>
            <Flexbox justifyContent="center" alignItems="center">
              <Text fontWeight="semiBold" as="span" color="white">
                Session is starting soon...
              </Text>
            </Flexbox>
          </VideoWrap>
        );
      }
      case 'active': {
        return (
          <VideoPlayerRTMP
            src={`https://stream.mux.com/${rtmpStream.mux_stream_playback_id}.m3u8`}
            sessionId={session._id}
            options={{ autoplay: true }}
            eventHandlers={{ ended: handleEnded }}
            muxMetaData={{
              viewer_user_id: userId,
              video_id: session._id,
              video_title: session.name,
            }}
          />
        );
      }
      case 'disabled': {
        return (
          <VideoWrap>
            <Flexbox justifyContent="center" alignItems="center">
              <Text size="h4_new" fontWeight="bold" as="span" color="white">
                This session has concluded
              </Text>
            </Flexbox>
          </VideoWrap>
        );
      }
      default: {
        throw new Error('RTMP status not implemented');
      }
    }
  };

  return (
    <>
      {renderStream()}
      <PusherHandler
        channel={rtmpStatusActive.channelName}
        event={rtmpStatusActive.eventType}
        onUpdate={() => {
          boundMutate(
            {
              ...rtmpStream,
              mux_stream_status: 'active',
              stream_started_at: new Date().toString(),
            },
            false,
          );
          mutate(
            `${VIDEO_API_URL}/${session._id}?isOrganizer=true`,
            {
              ...rtmpStream,
              mux_stream_status: 'active',
              stream_started_at: new Date().toString(),
            },
            false,
          );
        }}
      />
      <PusherHandler
        channel={rtmpStatusDisabled.channelName}
        event={rtmpStatusDisabled.eventType}
        onUpdate={() => {
          boundMutate({ ...rtmpStream, mux_stream_status: 'disabled' }, false);
          mutate(
            `${VIDEO_API_URL}/${session._id}?isOrganizer=true`,
            {
              ...rtmpStream,
              mux_stream_status: 'disabled',
              stream_ended_at: new Date().toString(),
            },
            false,
          );
        }}
      />
    </>
  );
};

export default Rtmp;
